// Client wrapper over the `generate-recipe` edge function.
//
// Keeps the components out of the supabase.functions.invoke plumbing
// and surfaces a user-readable message when the edge function errors.
// The edge function returns the recipe in the same shape as the bundled
// recipes (src/data/recipes/schema.js) so callers can pass the result
// straight into CookMode.

import { supabase } from "./supabase";
import { buildCanonicalRoster } from "./canonicalRoster";
import { dossierForRequest } from "../data/cuisineDossiers";
import { sanitizeRequestBody } from "./sanitizeForJson";

/**
 * Ask Claude to draft a recipe from the current pantry.
 *
 * Two modes:
 *   "final" (default) — full recipe with title + ingredients + steps.
 *                       Same shape as bundled recipes; CookMode-ready.
 *   "sketch"          — title + IDEAL + PANTRY ingredient lists, no
 *                       steps. Fast cheap pass for the tweak loop;
 *                       user reviews + swaps before we commit to a
 *                       full cook.
 *
 * @param {object} opts
 * @param {"sketch"|"final"} [opts.mode]     — default "final"
 * @param {Array<object>} opts.pantry        — curated pantry rows (see src/lib/aiContext.js)
 * @param {object}        [opts.prefs]       — { cuisine?, difficulty?, time?,
 *                                              mealPrompt?, mealTiming?, course?,
 *                                              starIngredientIds?, recipeFeedback? }
 * @param {Array<string>} [opts.avoidTitles] — recent drafts to steer away from on REGEN
 * @param {object}        [opts.context]     — rich context (profile slice + cook history
 *                                             summary); omit or pass null on REGEN to keep
 *                                             the model from re-anchoring on the same
 *                                             pairings as the first draft.
 * @param {Array<object>} [opts.lockedIngredients] — only meaningful in "final" mode;
 *                                             the user's tweaked ingredient list from
 *                                             the sketch phase. Claude builds steps
 *                                             around this set verbatim.
 * @param {object}        [opts.conceptFrame] — pre-picked concept frame from
 *                                             src/data/conceptFrames.js. When present,
 *                                             the edge fn renders the frame's prompt
 *                                             directive as a HARD CONSTRAINT block,
 *                                             pushing Claude off its modal answer (the
 *                                             Cacio-e-Pepe rut) into the frame's corner
 *                                             of recipe space.
 * @param {Array<string>} [opts.libraryTitles] — bundled + user-saved recipe titles. The
 *                                             edge fn lists these as forbidden when a
 *                                             frame is attached, so Claude doesn't
 *                                             redundantly suggest dishes the user
 *                                             already has access to. Carve-out: a typed
 *                                             SPECIFIC ask still overrides the forbid.
 * @returns {Promise<{ recipe?: object, sketch?: object }>}
 *          — `recipe` when mode = "final", `sketch` when mode = "sketch"
 */
/**
 * Classify a user's mealPrompt into a dish contract. Called once per
 * AIRecipe session (and re-called when mealPrompt changes) so sketch
 * and final both enforce against the same contract. Caching is the
 * caller's responsibility — the edge function does a ~300ms Haiku
 * call and doesn't cache server-side.
 *
 * @param {string} mealPrompt — the user's typed ask (may be "")
 * @returns {Promise<{
 *   tier: "SPECIFIC" | "FAMILY" | "OPEN" | "FREEFORM",
 *   dishName?: string, aliases?: string[], rules?: string,
 *   familyName?: string, familyExamples?: string[], rawPrompt?: string,
 * }>}
 */
export async function classifyDishPrompt(mealPrompt) {
  const { data, error } = await supabase.functions.invoke("generate-recipe", {
    body: {
      mode: "classify",
      prefs: { mealPrompt: typeof mealPrompt === "string" ? mealPrompt : "" },
    },
  });
  if (error || !data?.contract) {
    // Classifier failure is non-fatal — fall through to FREEFORM so
    // the draft still runs (just without deterministic post-check).
    // degraded:true tells ensureContract this is a FLAKE, not a real
    // classification — it must never be cached over a known-good
    // SPECIFIC contract (that silently kills the copycat trigger +
    // dish pinning for the rest of the session).
    return { tier: mealPrompt?.trim() ? "FREEFORM" : "OPEN", rawPrompt: mealPrompt || "", degraded: true };
  }
  return data.contract;
}

/**
 * Ask Haiku to pose 0-3 clarifying questions before the sketch fires.
 * Returns the (possibly empty) array of clarifications. Empty is the
 * normal answer when nothing's ambiguous — caller skips the clarify
 * UI phase in that case.
 *
 * One Haiku call, ~$0.005, ~700ms. Caller is expected to cache per
 * (mealPrompt, conceptFrame.id) so regens reuse the same questions.
 *
 * @param {object} args
 * @param {string} [args.mealPrompt]    — user's typed ask
 * @param {object} [args.conceptFrame]  — frame from src/data/conceptFrames.js
 * @param {object} [args.prefs]         — pass-through pref slice (course used)
 * @param {object} [args.context]       — pass-through (dietary read off context.profile)
 * @returns {Promise<{ clarifications: Array<{ id, question, options, chefChoiceLabel? }>,
 *                      funnel: { axis, moodQuestion, moods:[{id,label,lanes:[{id,label,blurb?}]}] } | null }>}
 *   The edge step returns EITHER a `funnel` (open-axis appetite explorer,
 *   e.g. "chicken with sauce") OR flat `clarifications` — never both.
 */
export async function requestClarifications({
  mealPrompt = "",
  conceptFrame = null,
  prefs = {},
  context = null,
  // Reroll support: { keepMoods: string[]|null, moods: string[],
  // lanes: string[] } — labels the user already saw. keepMoods pins
  // the same 4 moods (drilled-view reroll: fresh lanes only);
  // moods/lanes are never-repeat exclusions.
  funnelExclude = null,
} = {}) {
  const { data, error } = await supabase.functions.invoke("generate-recipe", {
    body: sanitizeRequestBody({
      mode: "clarify",
      prefs: {
        mealPrompt: typeof mealPrompt === "string" ? mealPrompt : "",
        course: typeof prefs.course === "string" ? prefs.course : null,
      },
      conceptFrame: conceptFrame || null,
      // Edge fn reads dietary off context.profile.dietary; mirror the
      // shape the sketch/final calls already send.
      context: context || null,
      ...(funnelExclude ? { funnelExclude } : {}),
    }, "generate-recipe:clarify"),
  });
  if (error || !data) {
    // Clarify failure is non-fatal — fall through to "no questions"
    // so the draft proceeds without the planning step. The user's
    // direct ask still steers the sketch.
    // eslint-disable-next-line no-console
    if (error) console.warn("[clarify] failed, skipping:", error.message || error);
    return { clarifications: [], funnel: null };
  }
  return {
    clarifications: Array.isArray(data.clarifications) ? data.clarifications : [],
    funnel: data.funnel && typeof data.funnel === "object" ? data.funnel : null,
  };
}

// Streaming variant of generateRecipe — only valid for mode === "final".
// Returns the same final payload as generateRecipe() resolves to, but
// also calls onChunk(text) for each text fragment Claude streams. The
// caller renders partial state during streaming for visual feedback;
// the resolved value (received via the SSE "complete" event) is the
// authoritative recipe.
//
// Why a separate function: supabase.functions.invoke() buffers the
// full response before resolving, which defeats streaming. We use
// raw fetch + ReadableStream.getReader() to consume the SSE bytes
// as they arrive.
export async function generateRecipeStreaming({
  pantry = [],
  prefs,
  avoidTitles,
  context,
  lockedIngredients,
  conceptFrame,
  libraryTitles,
  existingRecipe = null,          // REVISION path — see reviseRecipeStreaming
  stubId = null,                  // optional pending-stub id — when set, the edge
                                  // function persists the finished recipe to this
                                  // user_recipes row under EdgeRuntime.waitUntil, so
                                  // it survives the client disconnecting (app
                                  // backgrounded). See backgroundRecipeGen.js.
  onChunk,                        // (text: string) => void — called per token delta
  signal = null,                  // optional AbortSignal — lets a caller hard-timeout
                                  // a hung connection (the background-generation job
                                  // aborts after N seconds so a dropped stream surfaces
                                  // as a "failed → Retry" stub instead of spinning forever)
} = {}) {
  if (typeof onChunk !== "function") {
    throw new Error("generateRecipeStreaming requires an onChunk callback");
  }
  const canonicalRoster = buildCanonicalRoster();

  // Pull URL + anon key from the supabase client (set during init in
  // supabase.js). Using the client's own values rather than env vars
  // means streaming works in any deployment without extra config.
  const supabaseUrl = supabase?.supabaseUrl
    || process.env.REACT_APP_SUPABASE_URL
    || "";
  const supabaseAnonKey = supabase?.supabaseKey
    || process.env.REACT_APP_SUPABASE_ANON_KEY
    || "";
  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error("Supabase client is missing URL/key — can't stream");
  }
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token || supabaseAnonKey;

  const resp = await fetch(`${supabaseUrl}/functions/v1/generate-recipe`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "text/event-stream",
      "apikey": supabaseAnonKey,
      "Authorization": `Bearer ${accessToken}`,
    },
    // Same cycle-safe boundary as the non-streaming call — this
    // stringify was the other half of the "cannot serialize cyclic
    // structures" draft killer.
    body: JSON.stringify(sanitizeRequestBody({
      mode: "final",
      stream: true,
      pantry,
      prefs: prefs || {},
      avoidTitles: Array.isArray(avoidTitles) ? avoidTitles : [],
      context: context || null,
      lockedIngredients: Array.isArray(lockedIngredients) ? lockedIngredients : [],
      conceptFrame: conceptFrame || null,
      libraryTitles: Array.isArray(libraryTitles) ? libraryTitles : [],
      existingRecipe: existingRecipe || null,
      canonicalRoster,
      // Cuisine flavor dossier (A1) — the tradition's signature (base/
      // acid/heat/balance/phases/finish + authenticity anchors) when the
      // frame or the typed prompt names a cuisine we have a dossier for.
      // Null otherwise; server renders it in the volatile section.
      cuisineDossier: dossierForRequest(conceptFrame, prefs?.mealPrompt) || null,
      ...(stubId ? { stubId } : {}),
    }, "generate-recipe:stream")),
    ...(signal ? { signal } : {}),
  });

  if (!resp.ok || !resp.body) {
    let detail = "";
    try { detail = await resp.text(); } catch { /* swallow */ }
    // Metered denial (docs/AI_TOKENS.md) — surface the human sentence
    // ("resets Monday" / "unlock in about an hour"), not raw JSON.
    let tokenDenialMsg = "";
    try {
      const parsed = JSON.parse(detail);
      if (parsed?.error === "ai_tokens_exhausted" && parsed?.message) {
        tokenDenialMsg = String(parsed.message);
      }
    } catch { /* not JSON — keep the raw detail */ }
    if (tokenDenialMsg) throw new Error(tokenDenialMsg);
    throw new Error(`Recipe draft failed: HTTP ${resp.status}${detail ? ` — ${detail.slice(0, 300)}` : ""}`);
  }

  // Backward-compat path. If the edge function hasn't been redeployed
  // with the streaming branch yet, it ignores `stream: true` and
  // returns one buffered JSON payload. Detect that via Content-Type
  // and parse the whole body as JSON — gives a graceful degrade
  // (no streaming, but at least the recipe lands) until deploy.
  const contentType = resp.headers.get("content-type") || "";
  if (!contentType.includes("event-stream")) {
    const body = await resp.text();
    let parsed = null;
    try { parsed = JSON.parse(body); } catch { /* fall through */ }
    if (parsed?.recipe) {
      // eslint-disable-next-line no-console
      console.info("[generate-recipe] streaming endpoint returned buffered JSON — running degraded path. Redeploy generate-recipe to enable streaming.");
      return parsed;
    }
    throw new Error(
      "Streaming endpoint returned non-streaming response. " +
      "Redeploy with `supabase functions deploy generate-recipe` to enable streaming. " +
      `Body: ${body.slice(0, 200)}`,
    );
  }

  // Read the SSE stream. We accumulate bytes in `buf`, split on the
  // double-newline event separator, and parse each event for either
  // a "chunk" (call onChunk) or "complete"/"error" (resolve/reject).
  // Also accumulate fullText from chunks so we can salvage a partial
  // recipe if the stream cuts off before a complete event arrives
  // (Supabase timeout, network blip, etc.).
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let completePayload = null;
  let errorPayload = null;
  let fullText = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const messages = buf.split("\n\n");
    buf = messages.pop() || "";
    for (const msg of messages) {
      let eventType = null;
      let dataLine = "";
      for (const line of msg.split("\n")) {
        if (line.startsWith("event: ")) eventType = line.slice(7).trim();
        else if (line.startsWith("data: ")) dataLine += line.slice(6);
      }
      if (!dataLine) continue;
      let parsed = null;
      try { parsed = JSON.parse(dataLine); } catch { continue; }
      if (eventType === "chunk") {
        if (parsed?.text) {
          fullText += parsed.text;
          onChunk(parsed.text);
        }
      } else if (eventType === "complete") {
        completePayload = parsed;
      } else if (eventType === "error") {
        errorPayload = parsed;
      }
    }
  }

  if (errorPayload) {
    const detail = errorPayload?.detail || errorPayload?.error || "unknown error";
    throw new Error(`Recipe draft failed: ${String(detail).slice(0, 400)}`);
  }
  if (completePayload?.recipe) {
    if (Array.isArray(completePayload.droppedFields) && completePayload.droppedFields.length > 0) {
      // eslint-disable-next-line no-console
      console.warn("[generate-recipe] server dropped fields:", completePayload.droppedFields);
    }
    return completePayload;
  }

  // Stream ended without a complete event. Best-effort salvage: if
  // we accumulated enough text via chunk events, try parsing it as
  // a recipe so the user doesn't lose their generation. The server's
  // post-processing (canonical coercion, normalization, etc.) didn't
  // run, but the client-side coerceRecipeCanonicalIds path covers
  // the most important normalization downstream.
  if (fullText.length > 200) {
    let salvaged = null;
    const cleaned = fullText.replace(/```json\s*|\s*```/g, "").trim();
    try { salvaged = JSON.parse(cleaned); }
    catch {
      const first = cleaned.indexOf("{");
      const last  = cleaned.lastIndexOf("}");
      if (first >= 0 && last > first) {
        try { salvaged = JSON.parse(cleaned.slice(first, last + 1)); } catch { /* swallow */ }
      }
    }
    if (salvaged?.title && Array.isArray(salvaged.ingredients) && Array.isArray(salvaged.steps)) {
      // eslint-disable-next-line no-console
      console.warn("[generate-recipe] stream ended without complete event; salvaged from accumulated text", {
        textLength: fullText.length,
      });
      return { recipe: salvaged, droppedFields: ["__salvaged_from_stream"] };
    }
  }
  throw new Error(
    `Recipe draft stream ended without a complete event` +
    (fullText.length > 0
      ? ` (received ${fullText.length} chars but couldn't salvage a recipe — try again)`
      : ` (server dropped the connection — try again)`),
  );
}

export async function generateRecipe({
  mode = "final",
  pantry = [],
  prefs,
  avoidTitles,
  context,
  lockedIngredients,
  conceptFrame,
  libraryTitles,
} = {}) {
  // Canonical roster — see src/lib/canonicalRoster.js. Sent on every
  // call so Claude can bind ingredient text → canonical_id at
  // generation time, especially in dream mode where the pantry is
  // empty and there's no other anchor to our id space. Static across
  // calls, so the edge function caches it via prompt caching.
  const canonicalRoster = buildCanonicalRoster();

  // Cycle-safe boundary — supabase-js stringifies the body verbatim,
  // and ONE cyclic node anywhere in pantry/context/conceptFrame sank
  // the whole draft with "cannot serialize cyclic structures"
  // (constant user-reported failure on retry). The sanitizer breaks
  // cycles, drops DOM/event smugglers, and console.warns the exact
  // payload path so the contaminated state names itself.
  const { data, error } = await supabase.functions.invoke("generate-recipe", {
    body: sanitizeRequestBody({
      mode,
      pantry,
      prefs: prefs || {},
      avoidTitles: Array.isArray(avoidTitles) ? avoidTitles : [],
      context: context || null,
      lockedIngredients: Array.isArray(lockedIngredients) ? lockedIngredients : [],
      conceptFrame: conceptFrame || null,
      libraryTitles: Array.isArray(libraryTitles) ? libraryTitles : [],
      canonicalRoster,
      // Cuisine flavor dossier (A1) — the tradition's signature (base/
      // acid/heat/balance/phases/finish + authenticity anchors) when the
      // frame or the typed prompt names a cuisine we have a dossier for.
      // Null otherwise; server renders it in the volatile section.
      cuisineDossier: dossierForRequest(conceptFrame, prefs?.mealPrompt) || null,
    }, "generate-recipe"),
  });

  if (error) {
    // supabase-js v2 wraps the upstream Response in error.context.
    // Reading the body is async (it's a ReadableStream) so the old
    // sync parse missed it and we surfaced Supabase's generic
    // "Edge Function returned a non-2xx status code" instead of the
    // actual detail from the edge function's error payload.
    let detail = "";
    let rawFromModel = "";
    let tokenDenialMsg = "";
    const ctx = error.context;
    const consumeBody = async () => {
      // Prefer Response.text() — supabase-js usually wraps the
      // upstream Response in error.context so this is the happy path.
      if (ctx && typeof ctx.text === "function") {
        try { return await ctx.text(); } catch { /* fall through */ }
      }
      // Try Response.body.text() — under some Capacitor / fetch
      // polyfills the body itself is a Response-like object.
      if (ctx?.body && typeof ctx.body.text === "function") {
        try { return await ctx.body.text(); } catch { /* fall through */ }
      }
      // String body — pass through.
      if (typeof ctx?.body === "string") return ctx.body;
      // Plain-object body — JSON-encode. Wrapped in try/catch
      // because some fetch polyfills hand back a Response /
      // ReadableStream / Headers object whose enumerable graph holds
      // a back-reference to `window`. JSON.stringify on that throws
      // "Converting circular structure to JSON" and the actual edge
      // function detail gets swallowed by a misleading client-side
      // error message.
      if (ctx?.body && typeof ctx.body === "object") {
        try { return JSON.stringify(ctx.body); }
        catch { /* fall through to message extraction */ }
      }
      // Final fallback — surface whatever readable bits the error
      // object carries so the user isn't staring at "unknown error".
      return ctx?.statusText
          || ctx?.message
          || error?.message
          || "";
    };
    const text = await consumeBody();
    if (text) {
      try {
        const parsed = JSON.parse(text);
        // Metered denial (docs/AI_TOKENS.md) — the message field is
        // the human-readable line; prefer it over the error code.
        // (Stashed, not thrown — a throw here would be swallowed by
        // this block's own catch and read as unparseable text.)
        if (parsed?.error === "ai_tokens_exhausted" && parsed?.message) {
          tokenDenialMsg = String(parsed.message);
        }
        detail = parsed?.detail || parsed?.error || text;
        // When the edge fn 502s because the model returned non-JSON,
        // the raw model output rides in parsed.raw — surface a snippet
        // so the user can tell it was a model hiccup vs an infra
        // issue without opening devtools.
        if (parsed?.raw) {
          rawFromModel = String(parsed.raw).slice(0, 200);
        }
      } catch {
        detail = text;
      }
    }
    if (tokenDenialMsg) {
      // eslint-disable-next-line no-console
      console.error("[generate-recipe] AI-token denial", { detail });
      throw new Error(tokenDenialMsg);
    }
    const msgBase = detail
      ? `Recipe draft failed: ${String(detail).slice(0, 400)}`
      : `Recipe draft failed: ${error.message || "unknown error"}`;
    const msg = rawFromModel
      ? `${msgBase}\n\nModel said: ${rawFromModel}${rawFromModel.length >= 200 ? "…" : ""}`
      : msgBase;
    // Log the full detail so it's accessible in devtools even when
    // the UI truncates it.
    // eslint-disable-next-line no-console
    console.error("[generate-recipe] edge fn failure", { error, detail, rawFromModel });
    throw new Error(msg);
  }

  // Surface server-side normalizer drops to the console so the user
  // can see when prepSteps / reheat / etc. were silently filtered
  // out of the model's output. Helps debug "why didn't my prep
  // notification fire" without paging through edge function logs.
  if (Array.isArray(data?.droppedFields) && data.droppedFields.length > 0) {
    // eslint-disable-next-line no-console
    console.warn("[generate-recipe] server dropped fields:", data.droppedFields);
  }

  // Sketch responses come back as { sketch }; final as { recipe }.
  // If sketch mode receives a { recipe } (the pre-Phase-2 edge
  // function that ignores the mode flag), treat it as a graceful
  // fallback — the caller can land straight in preview skipping
  // the tweak phase. Signals this via data.fellBackToFinal = true.
  if (mode === "sketch") {
    if (!data?.sketch) {
      if (data?.recipe) {
        return { sketch: null, recipe: data.recipe, fellBackToFinal: true };
      }
      throw new Error("Sketch succeeded but response was empty");
    }
  } else {
    if (!data?.recipe) throw new Error("Recipe draft succeeded but response was empty");
  }

  return data;
}

// ── Revise an existing saved recipe via text feedback ───────────────
//
// "Too spicy — take the heat down" / "didn't rise — fix the
// leavening". Wraps the streaming final pass with the REVISION
// contract: the full current recipe rides in (compacted — the edge
// fn bounds it again server-side), the dish identity anchors via
// sketchTitle/sketchSubtitle, and the user's words land in
// prefs.recipeFeedback. Deliberately sends NO lockedIngredients —
// the locked contract's "use ALL verbatim" language would make
// "make it vegetarian" impossible; the REVISION block's
// "preserve unless implicated" semantics are the right anchor.
// No pantry / context either: the dish is fixed, re-surveying the
// kitchen is wasted tokens (egress rule 6).
//
// PAID AI — callers must gate this behind an explicit user tap.

// Bound a string without amputating mid-word: prefer the last
// sentence end within the cap, fall back to the last space.
function sliceAtSentence(text, max) {
  const s = String(text || "");
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const lastSentence = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("! "), cut.lastIndexOf("? "));
  if (lastSentence > max * 0.5) return cut.slice(0, lastSentence + 1);
  const lastSpace = cut.lastIndexOf(" ");
  return lastSpace > 0 ? cut.slice(0, lastSpace) : cut;
}

function compactExistingRecipe(recipe) {
  const r = recipe || {};
  return {
    title: String(r.title || "Recipe").slice(0, 200),
    subtitle: r.subtitle ? String(r.subtitle).slice(0, 200) : null,
    serves: Number.isFinite(Number(r.serves)) ? Number(r.serves) : null,
    cuisine: r.cuisine ? String(r.cuisine).slice(0, 60) : null,
    ingredients: (Array.isArray(r.ingredients) ? r.ingredients : [])
      .slice(0, 40)
      .map((i) => ({
        amount: i?.amount ? String(i.amount).slice(0, 60) : null,
        item: String(i?.item || i?.name || "").slice(0, 120),
        ingredientId: i?.ingredientId || null,
      }))
      .filter((i) => i.item),
    steps: (Array.isArray(r.steps) ? r.steps : [])
      .slice(0, 30)
      .map((s) => ({
        id: s?.id || null,
        title: s?.title ? String(s.title).slice(0, 120) : null,
        // Revision quality depends on the model seeing the WHOLE
        // step — the old 300-char mid-word slice fed it amputated
        // instructions, so "fix step 4" rewrote from half the
        // information. Bound generously, cut on sentence ends.
        instruction: sliceAtSentence(s?.instruction, 900),
      }))
      .filter((s) => s.instruction),
  };
}

export async function reviseRecipeStreaming({ recipe, feedback, onChunk }) {
  const text = String(feedback || "").trim();
  if (!text) throw new Error("reviseRecipeStreaming requires feedback text");
  if (!recipe || !Array.isArray(recipe.ingredients)) {
    throw new Error("reviseRecipeStreaming requires a full recipe object");
  }
  return generateRecipeStreaming({
    pantry: [],
    context: null,
    avoidTitles: [],
    prefs: {
      recipeFeedback: text,
      sketchTitle: recipe.title || undefined,
      sketchSubtitle: recipe.subtitle || undefined,
    },
    existingRecipe: compactExistingRecipe(recipe),
    onChunk,
  });
}
